import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { QRCodeSVG } from "qrcode.react";
import JsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import Error from "../../components/Error/Error";
import EorDetails from "../../components/EorDetails/EorDetails";

import usePaymentUpdates from "../../services/hooks/usePaymentUpdates";
import useLocalStorage from "../../services/hooks/useLocalStorage";
import appConfig, {
  formatPhpAmount,
  getPaymentStatusText,
} from "../../config/app.config";

import "./Status.css";

// status page
function Status() {
  const location = useLocation();
  const navigate = useNavigate();
  const { transData, userData } = location.state || {};
  const { data, connection } = usePaymentUpdates(
    transData?.merchant_reference_number || userData?.billing_no,
    transData,
  );
  const [getStoreBillNo] = useLocalStorage("billNo", "");

  const { qcuserid } = useParams();

  // Use centralized config for payment status mapping
  const paymentStatus = getPaymentStatusText(data?.status);
  const statusTone = String(data?.status || "").toLowerCase();

  /**
   * generates a PDF file containing payment information such as merchant reference
   * number, e-payment reference number, payment option, total amount, and payment status.
   */
  const pdfGenerate = () => {
    const doc = new JsPDF("landscape", "px", "a4", "false");

    autoTable(doc, {
      styles: { fontSize: 20 },
      head: [["Category", "Value"]],
      body: [
        [
          "Merchant Reference No",
          data.merchant_reference_number || getStoreBillNo,
        ],
        ["E-payment Reference No", data.epayment_reference_number || "N/A"],
        ["Payment Option", data.payment_option || "N/A"],
        ["Total Amount", formatPhpAmount(data.epayment_amount)],
        ["Payment Status", `${paymentStatus}`],
      ],
    });

    doc.save(
      `Payment Status_${data.merchant_reference_number || getStoreBillNo}.pdf`,
    );
  };

  let content;

  if (data) {
    content = (
      <section className="status border--blue">
        <div className="status-heading">
          <p className="status-eyebrow">PAYMENT STATUS</p>
          <h1>Transaction status</h1>
          <p>
            Keep this page for your records. Your payment status refreshes
            automatically.
          </p>
        </div>
        {connection === "reconnecting" && (
          <p role="status">Reconnecting to payment updates…</p>
        )}
        {connection === "denied" && (
          <p role="alert">
            Unable to access payment updates. Please reopen your payment link.
          </p>
        )}
        <Row className="status-main">
          <Col className="status-summary" xl={7} xs={12}>
            <div className="status-summary-card">
              <div className="status-card-heading">
                <h2>Transaction details</h2>
                <span
                  className={`status-badge status-badge-${statusTone}`}
                  data-testid="status-badge"
                >
                  {paymentStatus}
                </span>
              </div>
              <TableContainer className="status-table-container">
                <Table aria-label="Transaction details">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        className="status-label"
                        component="th"
                        scope="row"
                      >
                        Merchant reference number
                      </TableCell>
                      <TableCell className="status-value" align="right">
                        {data.merchant_reference_number || getStoreBillNo}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        className="status-label"
                        component="th"
                        scope="row"
                      >
                        E-payment reference number
                      </TableCell>
                      <TableCell className="status-value" align="right">
                        {data.epayment_reference_number || "N/A"}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        className="status-label"
                        component="th"
                        scope="row"
                      >
                        Payment option
                      </TableCell>
                      <TableCell className="status-value" align="right">
                        {data.payment_option || "N/A"}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        className="status-label"
                        component="th"
                        scope="row"
                      >
                        Total amount
                      </TableCell>
                      <TableCell className="status-amount" align="right">
                        {formatPhpAmount(data.epayment_amount)}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        className="status-label"
                        component="th"
                        scope="row"
                      >
                        Payment status
                      </TableCell>
                      <TableCell align="right">
                        <span
                          className={`status-badge status-badge-${statusTone}`}
                        >
                          {paymentStatus}
                        </span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <EorDetails details={data.eor_details} />
            </div>
          </Col>
          <Col className="status-qrcode" xl={5} xs={12}>
            <div className="status-qr-card">
              <p className="status-eyebrow">PAYMENT RECEIPT</p>
              <h2>Save your transaction</h2>
              <p>
                Scan this QR code or download a PDF copy for quick access to
                your payment details.
              </p>
              <div className="status-qr-code">
                <QRCodeSVG
                  id={1}
                  value={JSON.stringify({
                    MerchantRefNo:
                      data.merchant_reference_number || getStoreBillNo,
                    EpaymentRefNo: data.epayment_reference_number || "N/A",
                    PaymentOption: data.payment_option || "N/A",
                    Amount: formatPhpAmount(data.epayment_amount),
                    Status: paymentStatus,
                  })}
                  size={appConfig.ui.qrCodeSize}
                  level={appConfig.ui.qrCodeLevel}
                />
              </div>
              <div className="status-divider">or</div>
              <Button
                onClick={() => pdfGenerate()}
                className="status-download-button"
                variant="contained"
                size="large"
                disableElevation
                data-testid="status-download-pdf-button"
              >
                Download PDF
              </Button>
            </div>
          </Col>
        </Row>
        <button
          type="button"
          className="status-back-button"
          onClick={() =>
            navigate(`/information/${qcuserid}`, { state: { userData } })
          }
          data-testid="status-back-button"
        >
          <ArrowBackIcon />
          Back to payment details
        </button>
      </section>
    );
    /* Setting the `content` variable to an error page if the `data` object is
  falsy. It renders an `Error` component with a header, description, and a button 
  to return to the previous page. This is used to handle cases where the user does 
  not have permission to view the page or if there is an error fetching the transaction data. */
  } else {
    content = (
      <Error
        header="Access Denied"
        description="You don't have permission to view this site"
        btnTxt="RETURN"
      />
    );
  }
  return content;
}

export default Status;
